import { classNames } from "@/lib/format";
import {
  INFO_SCREEN_DESIGNS,
  createPageFromDesign,
  getDesignProposal,
  type DesignProposal,
} from "@/pages/info-screen/designs";
import type { InfoScreenDesign, InfoScreenPage } from "@/types/infoScreen";

type Props = {
  selectedDesign?: InfoScreenDesign;
  canEdit: boolean;
  onSelectDesign: (designId: InfoScreenDesign) => void;
  onCreateFromDesign: (page: InfoScreenPage) => void;
};

function DesignCard({
  proposal,
  selected,
  disabled,
  onSelect,
  onCreate,
}: {
  proposal: DesignProposal;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onCreate: () => void;
}) {
  return (
    <button
      type="button"
      className={classNames(
        "info-screen-design-card",
        selected && "info-screen-design-card--selected",
      )}
      disabled={disabled}
      onClick={onSelect}
      onDoubleClick={() => {
        if (!disabled) onCreate();
      }}
      title={
        disabled
          ? proposal.name
          : `${proposal.name} – einfach klicken zum Auswählen, Doppelklick für neue Seite`
      }
    >
      <div
        className="info-screen-design-card__preview"
        data-layout={proposal.layout}
        style={{ background: proposal.previewGradient }}
      >
        <div className="info-screen-design-card__preview-shapes" aria-hidden>
          <div className="info-screen-design-card__shape-media" />
          <div className="info-screen-design-card__shape-text">
            <div className="info-screen-design-card__shape-line" />
            <div className="info-screen-design-card__shape-line info-screen-design-card__shape-line--short" />
          </div>
        </div>
        <div
          className="info-screen-design-card__preview-accent"
          style={{ background: proposal.previewAccent }}
        />
      </div>
      <div className="info-screen-design-card__body">
        <p className="info-screen-design-card__name">{proposal.name}</p>
        <p className="info-screen-design-card__desc">{proposal.description}</p>
        <div className="info-screen-design-card__tags">
          {proposal.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="info-screen-design-card__tag">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}

export function InfoScreenDesignGallery({
  selectedDesign = "classic",
  canEdit,
  onSelectDesign,
  onCreateFromDesign,
}: Props) {
  const proposals = INFO_SCREEN_DESIGNS.filter((d) => d.id !== "classic");

  function createPage(designId: InfoScreenDesign) {
    const proposal = getDesignProposal(designId);
    const page = createPageFromDesign(designId, proposal.name, 0);
    onCreateFromDesign(page);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h4 className="text-sm font-medium">Design-Vorschläge</h4>
          <p className="mt-0.5 text-sm text-label-secondary">
            {proposals.length} Stile für Empfang, Abend und Outdoor. Klicken wählt das Design
            für die aktuelle Seite; Doppelklick legt eine neue Seite an.
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            className="text-sm font-medium text-accent hover:underline"
            onClick={() => createPage(selectedDesign === "classic" ? "glass" : selectedDesign)}
          >
            Neue Seite mit „{getDesignProposal(selectedDesign === "classic" ? "glass" : selectedDesign).name}“
          </button>
        ) : null}
      </div>
      <div className="info-screen-design-gallery">
        {proposals.map((proposal) => (
          <DesignCard
            key={proposal.id}
            proposal={proposal}
            selected={selectedDesign === proposal.id}
            disabled={!canEdit}
            onSelect={() => onSelectDesign(proposal.id)}
            onCreate={() => createPage(proposal.id)}
          />
        ))}
      </div>
    </div>
  );
}
